import { ReactNode, ComponentPropsWithoutRef } from 'react';

import styled from 'styled-components';

import { Tooltip, TooltipProps } from '@components/tooltip';
import { Color, theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

export type BadgeSize = 'sm' | 'md' | 'lg';

export type BaseBadgeProps = ComponentPropsWithoutRef<'div'> & {
  children: ReactNode;
  color?: Color;
  bg?: Color;
  size?: BadgeSize;
  rounded?: boolean;
  tooltip?: string | Omit<TooltipProps, 'children'>;
  fraction?: boolean;
};

export const BaseBadge = ({
  children,
  size = 'md',
  color = 'textSecondary',
  bg = 'uiTrans10',
  rounded = false,
  tooltip,
  fraction = false,
  ...rest
}: BaseBadgeProps) => {
  const content = (
    <BadgeContainer
      $size={size}
      $color={color}
      $rounded={rounded}
      $bg={bg}
      $fraction={fraction}
      {...rest}
    >
      {children}
    </BadgeContainer>
  );

  if (!tooltip) return content;

  if (typeof tooltip === 'string') {
    return <Tooltip content={tooltip}>{content}</Tooltip>;
  }

  return <Tooltip {...tooltip}>{content}</Tooltip>;
};
export type BadgeVariant =
  | 'primary-blue'
  | 'secondary'
  | 'secondary-red'
  | 'secondary-green'
  | 'active'
  | 'inactive'
  | 'error'
  | 'ghost';
export type BadgeProps = Omit<BaseBadgeProps, 'color'> & {
  variant?: BadgeVariant;
};

export const Badge = ({ variant = 'secondary', ...rest }: BadgeProps) => {
  return <BaseBadge {...variantMap[variant]} {...rest} />;
};

const variantMap: Record<BadgeVariant, Pick<BaseBadgeProps, 'bg' | 'color'>> = {
  active: { color: 'white100', bg: 'elementGreen100' },
  error: { color: 'white100', bg: 'elementRed100' },
  'primary-blue': { color: 'white100', bg: 'elementBlue100' },
  secondary: { color: 'textSecondary', bg: 'uiTrans10' },
  'secondary-red': { color: 'textSecondary', bg: 'redTrans20' },
  'secondary-green': { color: 'textSecondary', bg: 'greenTrans20' },
  inactive: { color: 'white100', bg: 'elementPurple50' },
  ghost: { color: 'textSecondary', bg: 'inherit' },
};

const BadgeContainer = styled.div<{
  $size: BadgeSize;
  $color: Color;
  $rounded: boolean;
  $bg: Color;
  $fraction: boolean;
}>((p) => ({
  flexShrink: 0,
  padding: p.$fraction ? `${pxToRem(4)} ${pxToRem(8)}` : paddingMap[p.$size],
  overflow: 'hidden',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: sizeMap[p.$size],
  height: sizeMap[p.$size],
  color: theme.color[p.$color],
  background: theme.color[p.$bg],
  borderRadius: p.$rounded ? 9999 : radiusMap[p.$size],
  font: fontMap[p.$size],
}));

const paddingMap: Record<BadgeSize, string> = {
  sm: `${pxToRem(2)} ${pxToRem(4)}`,
  md: pxToRem(4),
  lg: `${pxToRem(4)} ${pxToRem(8)}`,
};

const sizeMap: Record<BadgeSize, string> = {
  sm: pxToRem(16),
  md: pxToRem(24),
  lg: pxToRem(40),
};

const radiusMap: Record<BadgeSize, string> = {
  sm: pxToRem(2),
  md: pxToRem(4),
  lg: pxToRem(4),
};

const fontMap: Record<BadgeSize, string> = {
  sm: theme.font.semibold10,
  md: theme.font.semibold12,
  lg: theme.font.bold16,
};
